import { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import { saveFeedback, getFeedbackCount } from '../services/feedbackService';
import { CURRENT_FEEDBACK_EVENT as CURRENT_EVENT, isFeedbackOpen } from '../config/events';
import { sanitizeFormData } from '../utils/inputValidation';
import ErrorMessage from './ErrorMessage';
import { DISABILITY_OPTIONS } from '../config/disabilityOptions';

const FeedbackForm = () => {
  const [formData, setFormData] = useState({
    phoneNumber: '',
    name: '',
    age: '',
    disability: '',
    disabilityOther: '',
    disabilityIsOther: false,
    cityOfResidence: '',
    whatYouEnjoyed: '',
    improvementSuggestions: '',
    attendAgain: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('form');
  const [isFull, setIsFull] = useState(false);
  const [checkingCapacity, setCheckingCapacity] = useState(true);
  // Checked on load and again on submit (closesAt may pass while the page is open).
  const [isOpen, setIsOpen] = useState(() => isFeedbackOpen(CURRENT_EVENT));

  useEffect(() => {
    const checkCapacity = async () => {
      const count = await getFeedbackCount(CURRENT_EVENT.id);
      setIsFull(count >= CURRENT_EVENT.feedback.cap);
      setCheckingCapacity(false);
    };
    checkCapacity();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({ ...prev, phoneNumber: '+' + value }));
    setError('');
  };

  const handleDisabilityChange = (e) => {
    const value = e.target.value;
    const isOther = value === 'other';
    setFormData(prev => ({
      ...prev,
      disability: isOther ? '' : value,
      disabilityIsOther: isOther,
      disabilityOther: isOther ? prev.disabilityOther : ''
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.phoneNumber || formData.phoneNumber === '+') return 'Please enter your mobile number';
    if (!formData.name.trim()) return 'Please enter your full name';
    if (!formData.age || isNaN(formData.age) || Number(formData.age) < 18) return 'You must be 18 or above to submit feedback';
    if (!formData.disability && !formData.disabilityIsOther) return 'Please select your disability or condition';
    if (formData.disabilityIsOther && !formData.disabilityOther.trim()) return 'Please describe your disability or condition';
    if (!formData.cityOfResidence.trim()) return 'Please enter your city of residence';
    if (!formData.whatYouEnjoyed.trim()) return 'Please share what you enjoyed';
    if (!formData.improvementSuggestions.trim()) return 'Please share your suggestions';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }
    if (!isFeedbackOpen(CURRENT_EVENT)) { setIsOpen(false); return; }

    setLoading(true);
    setError('');

    const cleanData = sanitizeFormData(formData);
    const result = await saveFeedback(cleanData, CURRENT_EVENT);

    if (result.success) {
      setStep('success');
      document.getElementById('sr-announcement').textContent = 'Feedback submitted successfully. Thank you!';
    } else {
      setError(result.error || 'Failed to submit feedback. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="registration-container">
      <div id="sr-announcement" className="sr-only" role="status" aria-live="polite" aria-atomic="true"></div>

      {checkingCapacity ? (
        <div className="checking-capacity"><p>Loading feedback form...</p></div>

      ) : !isOpen ? (
        <div className="registration-closed">
          <div className="closed-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
          </div>
          <h2>Feedback Closed</h2>
          <p className="closed-message">Feedback for this event is no longer being accepted.</p>
          <p className="stay-tuned">Stay tuned for future Buddy Up community events and opportunities to connect.</p>
          <div className="closed-cta"><p>Download our app to stay updated on upcoming events</p></div>
        </div>

      ) : isFull ? (
        <div className="registration-closed">
          <div className="closed-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
          </div>
          <h2>Feedback Limit Reached</h2>
          <p className="closed-message">We've received the maximum number of responses for this event. Thank you!</p>
          <p className="stay-tuned">Stay tuned for future Buddy Up community events and opportunities to connect.</p>
          <div className="closed-cta"><p>Download our app to stay updated on upcoming events</p></div>
        </div>

      ) : step === 'success' ? (
        <div className="success-message" role="alert">
          <h2>Thank You!</h2>
          <p>Your feedback has been submitted. We appreciate you sharing your experience with Buddy Up.</p>
        </div>

      ) : (
        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-title">
            <h1>{CURRENT_EVENT.name}</h1>
            <span className="form-type-badge">Feedback</span>
            <hr className="title-divider" />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Mobile Number <span aria-label="required">*</span></label>
            <PhoneInput
              country={'in'}
              value={formData.phoneNumber.replace('+', '')}
              onChange={handlePhoneChange}
              autoFormat={false}
              inputProps={{
                id: 'phone', name: 'phoneNumber',
                required: true, 'aria-required': 'true', autoComplete: 'tel'
              }}
              containerClass="phone-input-container"
              inputClass="phone-form-control"
              buttonClass="phone-dropdown-button"
              dropdownClass="phone-dropdown"
            />
          </div>

          <div className="form-group">
            <label htmlFor="name">Full Name <span aria-label="required">*</span></label>
            <input
              type="text" id="name" name="name"
              value={formData.name} onChange={handleInputChange}
              required aria-required="true" autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="age">Age <span aria-label="required">*</span></label>
            <input
              type="number" id="age" name="age"
              value={formData.age} onChange={handleInputChange}
              required aria-required="true" min="1" max="120"
            />
          </div>

          <div className="form-group">
            <label htmlFor="disability-select">
              Disability / Condition <span aria-label="required">*</span>
            </label>
            <select
              id="disability-select"
              name="disability"
              className="disability-native-select"
              value={formData.disabilityIsOther ? 'other' : formData.disability}
              onChange={handleDisabilityChange}
              required
              aria-required="true"
            >
              <option value="" disabled>Select your disability or condition</option>
              {DISABILITY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {formData.disabilityIsOther && (
              <input
                type="text"
                name="disabilityOther"
                value={formData.disabilityOther}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, disabilityOther: e.target.value, disability: e.target.value }));
                  setError('');
                }}
                placeholder="Please describe your disability or condition"
                aria-label="Describe your disability or condition"
                style={{ marginTop: '10px' }}
              />
            )}
          </div>

          <div className="form-group">
            <label htmlFor="cityOfResidence">City / Place of Residence <span aria-label="required">*</span></label>
            <input
              type="text" id="cityOfResidence" name="cityOfResidence"
              value={formData.cityOfResidence} onChange={handleInputChange}
              required aria-required="true" autoComplete="address-level2"
            />
          </div>

          <div className="form-group">
            <label htmlFor="whatYouEnjoyed">What did you enjoy most about the event? <span aria-label="required">*</span></label>
            <textarea
              id="whatYouEnjoyed" name="whatYouEnjoyed"
              value={formData.whatYouEnjoyed} onChange={handleInputChange}
              required aria-required="true" rows="3"
            />
          </div>

          <div className="form-group">
            <label htmlFor="improvementSuggestions">What would you like to see done differently? <span aria-label="required">*</span></label>
            <textarea
              id="improvementSuggestions" name="improvementSuggestions"
              value={formData.improvementSuggestions} onChange={handleInputChange}
              required aria-required="true" rows="3"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox" name="attendAgain"
                checked={formData.attendAgain} onChange={handleInputChange}
              />
              <span>I would like to attend future Buddy Up events</span>
            </label>
          </div>

          <ErrorMessage message={error} />

          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </form>
      )}
    </div>
  );
};

export default FeedbackForm;